/* eslint-disable camelcase */
import { format, parseISO } from 'date-fns';

import {
  ADDON_CODES,
  ADDON_CREDIT_BLOCKS,
  ADDON_PROJECTS_PAID_THRESHOLD,
  ANNUAL_BONUS_PERCENTAGE,
  CREDITS_PER_BLOCK,
  INTERVAL_TO_LABEL,
  NEXT_BILL_DATE_FORMAT,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_USAGE,
  SUBSCRIPTION_STATE,
  TESTING_TYPES,
  TESTING_TYPE_TO_TIER,
  UNIFIED_BILLING_TYPE,
} from '../constants';
import { Plan, PlanAddOn, ServerPlan, getProductType } from './Plan';
import type { components } from '../../types/billingRecurly.d';
import { creditsConsideringAnnualBonus } from '../ducks/plans/utils';

export type ServerSubscription = components['schemas']['Subscription'];

export type ServerSubscriptionAddon = components['schemas']['SubscriptionAddOn'];

class SubscriptionAddOn implements ServerSubscriptionAddon {
  constructor(attributes: Partial<ServerSubscriptionAddon> = {}) {
    Object.assign(this, { ...attributes });
  }
}

// trick: create interface with the same name as a class above so we don't
// need to redeclare the mmembeers
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SubscriptionAddOn extends ServerSubscriptionAddon {}

class SubscriptionPlan extends Plan {
  static parsePlan(rawPlan: ServerPlan): Plan {
    const addOns = (rawPlan.add_ons || []).map((rawAddOn) => new PlanAddOn(rawAddOn));
    return new SubscriptionPlan({ ...rawPlan, add_ons: addOns });
  }
}

const DEFAULT_SUBSCRIPTION: Partial<ServerSubscription> = {
  add_ons: [],
  current_period_ends_at: '',
  current_period_started_at: '',
  id: '',
};

const getAddonName = (productType: string): string => {
  switch (productType) {
    case PRODUCT_API_TESTING:
      return 'projects';
    case PRODUCT_USAGE:
      return 'creditBlocks';
    default:
      return 'concurrency';
  }
};

class Subscription implements ServerSubscription {
  constructor(attributes: Partial<ServerSubscription> = DEFAULT_SUBSCRIPTION) {
    Object.assign(this, { ...DEFAULT_SUBSCRIPTION, ...attributes });
  }

  static parseSubscription(rawSubscription: ServerSubscription): Subscription {
    const addOns = (rawSubscription.add_ons || [])
      .map((rawAddOn) => new SubscriptionAddOn(rawAddOn));
    const plan = SubscriptionPlan.parsePlan(rawSubscription.plan);

    return new Subscription({ ...rawSubscription, add_ons: addOns, plan });
  }

  displayName(): string {
    return this.plan.displayName();
  }

  getAddonAmount(): number {
    if (this.add_ons.length) {
      return this.add_ons
        .find((addOn) => ADDON_CODES.includes(addOn.code))
        ?.quantity as number;
    }

    if (this.plan.testing_type === TESTING_TYPES.API_TESTING) {
      return ADDON_PROJECTS_PAID_THRESHOLD;
    }
    // Legacy live testing plan doesn't have parallel_tests set:
    return Number.isFinite(this.plan.parallel_tests) ? this.plan.parallel_tests : 1;
  }

  getInterval(): IntervalUnit {
    return this.plan.interval_unit as IntervalUnit;
  }

  getIntervalLabel(): string {
    return INTERVAL_TO_LABEL[this.getInterval()];
  }

  isUnifiedPlan(): boolean {
    return this?.plan.plan_type === UNIFIED_BILLING_TYPE;
  }

  isMonthly(): boolean {
    return this.getInterval() === PLAN_INTERVAL.MONTH;
  }

  isAnnual(): boolean {
    return this.getInterval() === PLAN_INTERVAL.YEAR;
  }

  isActive(): boolean {
    return this.state === SUBSCRIPTION_STATE.ACTIVE;
  }

  displayStartedOnDate(): string {
    return format(parseISO(this.created_at), NEXT_BILL_DATE_FORMAT);
  }

  displayNextBillDate(): string {
    return format(parseISO(this.current_period_ends_at), NEXT_BILL_DATE_FORMAT);
  }

  displaySauceFeatureName(): string {
    return TESTING_TYPE_TO_TIER[this.plan.testing_type] || '';
  }

  getMonthlyPrice(): number {
    const finalPrice = this.final_price; // subtotal field in recurly
    return this.isMonthly() ? finalPrice : finalPrice / 12;
  }

  isEqualToPlanConfiguration(
    productType: any,
    planConfiguration: any,
  ): boolean {
    const addonName = getAddonName(productType);
    const addonAmount = this.getAddonAmount();
    const interval = this.getInterval();

    return (
      getProductType(this.plan) === productType &&
      addonAmount === planConfiguration[addonName] &&
      interval === planConfiguration.interval
    );
  }

  getCreditBlocksAmount(): number {
    if (this.add_ons.length) {
      const creditAddOn = this.add_ons.find(
        ({ code }) => code === ADDON_CREDIT_BLOCKS,
      );

      if (creditAddOn) {
        return creditAddOn.quantity;
      }
    }

    return 0;
  }

  getPendingChangeDate(): Date | null {
    if (!this.pending_change?.start_date) {
      return null;
    }

    return parseISO(this.pending_change?.start_date);
  }

  getPendingChangeCredits(): number {
    const creditAddOn = this.pending_change?.add_ons?.find(
      ({ code }) => code === ADDON_CREDIT_BLOCKS,
    );

    if (creditAddOn) {
      return creditsConsideringAnnualBonus(
        creditAddOn.quantity * CREDITS_PER_BLOCK,
        this.isAnnual(),
      );
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Subscription extends ServerSubscription {
  plan: Plan;
}

export default Subscription;
export { Subscription, SubscriptionPlan, SubscriptionAddOn };
